'use client';

/**
 * Aviation loaders (director 2026-09-27, picks 5a01 / 9f1d / a2e3): very subtle, no text
 * or numbers — soft shapes a maintenance crowd recognises. Styles live in globals.css
 * (`.av-*`). Under prefers-reduced-motion the scenes keep their motion (it stays inside the
 * loader's box); only the bar sweeps (shimmer, flow dot) pulse in place (see globals.css).
 *
 * Scenes (one quiet motion each):
 *  - turbine:   fan face of tapered blades, a soft blue glow travelling round it.
 *  - hydraulic: pump → line → actuator; the piston strokes out as fluid fills the barrel.
 *  - fuel:      wing tank drains to the engine, then a nozzle refuels it.
 *  - gauge:     instrument needle sweeping, with a drifting pressure trace.
 *  - gears:     two meshed gears turning slowly.
 * Plus FlowLine (a skeleton bar with a very faint blue dot gliding along it) and SkeletonBar.
 *
 * AviationScene picks one scene at random after mount; useSceneDeck deals several distinct
 * scenes so a page never shows the same one twice (director: turbine at most once).
 * `kind` says whether the slot is the main body ('system') or a small card ('part').
 */
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { FF_DASHBOARD_R5 } from '@/lib/featureFlags';

/** The legacy deck (flag OFF): one mixed set used everywhere. */
export const SCENES = ['turbine', 'hydraulic', 'fuel', 'gauge', 'gears'] as const;

/*
 * Skeleton corrections (director 2026-09-28; behind NEXT_PUBLIC_FF_DASHBOARD_R5, pearl run P5):
 *  - main-body loaders show a FULL SYSTEM, small cards show one PART — never the other way round;
 *  - motion is slow enough to read as a loading placeholder, not as busy UI (`.av-slow`);
 *  - the engine is a real turbofan face (swept wide-chord fan turning inside its nacelle);
 *  - five part skeletons: pulley, double-acting cylinder, transfer diode, accumulator, jackscrew;
 *  - systems read as real aircraft circuits (no household plug; flight controls are a direct
 *    column → cable → elevator run).
 * The 2026-09-26 concept PNGs were references only; every shape here is drawn from scratch.
 */
export const SYSTEM_SCENES = ['engine', 'hydraulic-system', 'fuel', 'electrical', 'flight-controls'] as const;
export const PART_SCENES = ['pulley', 'double-acting-cylinder', 'transfer-diode', 'accumulator', 'jackscrew', 'gauge', 'gears'] as const;
export type Scene = (typeof SCENES)[number] | (typeof SYSTEM_SCENES)[number] | (typeof PART_SCENES)[number];
/** Where a loader sits: the main body (a whole system) or a small card (one part). */
export type SceneKind = 'system' | 'part';

/** The deck a slot deals from. Flag OFF keeps the legacy mixed deck for every slot. */
export function scenesFor(kind: SceneKind, r5: boolean = FF_DASHBOARD_R5): readonly Scene[] {
  if (!r5) return SCENES;
  return kind === 'part' ? PART_SCENES : SYSTEM_SCENES;
}

const BLADES = 24;
const TURBINE_PERIOD = 1.8;

export function TurbineLoader({ size = 64 }: { size?: number }) {
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="48" className="av-soft-fill" />
      {Array.from({ length: BLADES }, (_, i) => (
        <path
          key={i}
          className="av-turbine__blade"
          d="M48.2 6 L52.6 6.6 L51.6 29 L49.2 29 Z"
          transform={`rotate(${(360 / BLADES) * i} 50 50)`}
          // Clockwise travel: each blade lights a step after the one before it.
          style={{ animationDelay: `${-((BLADES - i) / BLADES) * TURBINE_PERIOD}s` }}
        />
      ))}
      <circle cx="50" cy="50" r="19" className="av-body" />
    </svg>
  );
}

export function GaugeLoader({ size = 64 }: { size?: number }) {
  const ticks = Array.from({ length: 13 }, (_, i) => -120 + i * 20);
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      {/* Instrument face (2026-10-08): the needle sweeps over the body fill, never straight over
          the card's grey stage, where the dark glow was ~1.3:1. */}
      <circle cx="50" cy="50" r="46" className="av-body" />
      <path d="M17.1 69 A38 38 0 1 1 82.9 69" className="av-gauge__arc" />
      {ticks.map((a) => (
        <line key={a} x1="50" y1="14" x2="50" y2={a % 60 === 0 ? 21 : 18} className="av-stroke" transform={`rotate(${a} 50 50)`} />
      ))}
      <line x1="50" y1="50" x2="50" y2="20" className="av-gauge__needle" />
      <circle cx="50" cy="50" r="3.5" className="av-base-fill" />
    </svg>
  );
}

// Three-phase AC (director 2026-09-27): three sines 120° apart, drawn across twice the box
// width and slid left by one period — a seamless drift.
function wavePath(phase: number) {
  let d = '';
  for (let x = 0; x <= 400; x += 5) d += `${x === 0 ? 'M' : 'L'}${x} ${(20 + 11 * Math.sin((x / 100) * Math.PI + phase)).toFixed(2)} `;
  return d;
}
const WAVE_A = wavePath(0);
const WAVE_B = wavePath((2 * Math.PI) / 3);
const WAVE_C = wavePath((4 * Math.PI) / 3);

export function WaveTrace({ width = 120, height = 40 }: { width?: number | string; height?: number }) {
  return (
    <svg className="av-scene av-wave" width={width} height={height} viewBox="0 0 200 40" preserveAspectRatio="none" aria-hidden="true">
      {/* Trace screen: same reason as the gauge face. */}
      <rect x="1" y="1" width="198" height="38" className="av-body" vectorEffect="non-scaling-stroke" />
      <g className="av-wave__drift">
        <path d={WAVE_A} className="av-wave__a" />
        <path d={WAVE_B} className="av-wave__b" />
        <path d={WAVE_C} className="av-wave__c" />
      </g>
    </svg>
  );
}

// Fluid route pump → actuator; the dots ride behind the parts, so they vanish inside them.
const HYD_ROUTE = 'M30 64 H112';

/** Pump → line → actuator: the piston strokes out while fluid fills the barrel behind it. */
export function HydraulicLoader({ width = 200 }: { width?: number }) {
  return (
    <svg className="av-scene" width={width} height={width / 2} viewBox="0 0 200 100" aria-hidden="true">
      <path d={HYD_ROUTE} className="av-line" />
      {[0, 1, 2].map((i) => (
        // Delays scale with --av-pace (2 under .av-slow) so the dots stay evenly spaced when slowed.
        <circle key={i} r="2.4" className="av-dot" style={{ offsetPath: `path('${HYD_ROUTE}')`, animationDelay: `calc(${-i * 0.8}s * var(--av-pace, 1))` }} />
      ))}
      {/* Pump: rotor turns */}
      <circle cx="30" cy="64" r="13" className="av-body" />
      <g className="av-spin" style={{ transformOrigin: '30px 64px' }}>
        <line x1="30" y1="54" x2="30" y2="74" className="av-stroke" />
        <line x1="20" y1="64" x2="40" y2="64" className="av-stroke" />
      </g>
      {/* Filter */}
      <rect x="58" y="55" width="16" height="18" rx="3" className="av-body" />
      <line x1="62" y1="61" x2="70" y2="61" className="av-stroke" />
      <line x1="62" y1="67" x2="70" y2="67" className="av-stroke" />
      {/* Actuator barrel, fluid chamber, piston + rod */}
      <rect x="110" y="52" width="62" height="24" rx="4" className="av-body" />
      <rect x="113" y="55" width="6" height="18" rx="1.5" className="av-fluid av-hyd__chamber" />
      <g className="av-hyd__piston">
        <rect x="119" y="54" width="5" height="20" rx="1.5" className="av-base-fill" />
        <rect x="124" y="62" width="58" height="4" rx="2" className="av-base-fill" />
        <circle cx="184" cy="64" r="4" className="av-body" />
      </g>
    </svg>
  );
}

const FUEL_ROUTE = 'M72 72 H150';

/** Wing tank drains to the engine, then a nozzle refuels it. */
export function FuelLoader({ width = 200 }: { width?: number }) {
  // Unique per instance: several fuel scenes can share a page (skeleton grid).
  const clipId = `av-fuel-tank-${useId().replace(/:/g, '')}`;
  return (
    <svg className="av-scene" width={width} height={width / 2} viewBox="0 0 200 100" aria-hidden="true">
      <path d={FUEL_ROUTE} className="av-line" />
      {[0, 1, 2].map((i) => (
        <circle key={i} r="2.2" className="av-dot av-fuel__dot" style={{ offsetPath: `path('${FUEL_ROUTE}')`, animationDelay: `calc(${-i * 0.7}s * var(--av-pace, 1))` }} />
      ))}
      {/* Refuel nozzle + falling drops (only during the refuel phase) */}
      <path d="M36 8 V18 M30 18 H42" className="av-stroke" />
      {[0, 1].map((i) => (
        <circle key={i} cx="36" cy="22" r="1.8" className="av-fuel__drop" style={{ animationDelay: `calc(${i * 0.35}s * var(--av-pace, 1))` }} />
      ))}
      {/* Tank with draining / refilling level */}
      <rect x="10" y="30" width="62" height="52" rx="8" className="av-body" />
      <clipPath id={clipId}><rect x="12" y="32" width="58" height="48" rx="6" /></clipPath>
      <g clipPath={`url(#${clipId})`}>
        <g className="av-fuel__level">
          <path d="M-20 38 Q-10 35 0 38 T20 38 T40 38 T60 38 T80 38 T100 38 V90 H-20 Z" className="av-fluid av-slosh" />
        </g>
      </g>
      {/* Engine nacelle */}
      <rect x="150" y="58" width="42" height="28" rx="12" className="av-body" />
      <circle cx="160" cy="72" r="8" className="av-soft-fill" />
      <g className="av-spin" style={{ transformOrigin: '160px 72px' }}>
        <line x1="160" y1="66" x2="160" y2="78" className="av-stroke" />
        <line x1="154" y1="72" x2="166" y2="72" className="av-stroke" />
      </g>
    </svg>
  );
}

function gearPath(cx: number, cy: number, teeth: number, rOuter: number, rInner: number) {
  const step = (Math.PI * 2) / teeth;
  let d = '';
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    for (const [r, t] of [[rInner, a], [rOuter, a + step * 0.18], [rOuter, a + step * 0.48], [rInner, a + step * 0.66]]) {
      d += `${d ? 'L' : 'M'}${(cx + r * Math.cos(t)).toFixed(2)} ${(cy + r * Math.sin(t)).toFixed(2)} `;
    }
  }
  return `${d}Z`;
}
const GEAR_A = gearPath(38, 50, 12, 30, 24);
const GEAR_B = gearPath(85, 50, 8, 20, 15);

export function GearLoader({ width = 120 }: { width?: number }) {
  return (
    <svg className="av-scene" width={width} height={(width * 100) / 120} viewBox="0 0 120 100" aria-hidden="true">
      {/* Gearbox plate (2026-10-08): the teeth turn over the body fill, not the grey stage. */}
      <rect x="1" y="6" width="118" height="88" rx="10" className="av-body" />
      <g className="av-gears__a"><path d={GEAR_A} className="av-gear" /><circle cx="38" cy="50" r="9" className="av-body" /></g>
      <g className="av-gears__b"><path d={GEAR_B} className="av-gear av-gear--lit" /><circle cx="85" cy="50" r="5.5" className="av-body" /></g>
    </svg>
  );
}

// ─── Systems (main body) ────────────────────────────────────────────────────────────

// One wide-chord, swept fan blade pointing up from the spinner (root narrow, tip wide).
const FAN_BLADE = 'M47.6 37 C48 28 48.6 19.5 50 11.6 L57.4 12.6 C55.2 21 53.6 29 52.2 37 Z';
const FAN_BLADES = 18;

/** Turbofan face: the swept fan turns slowly inside the nacelle; one lit blade and the
 *  spinner swirl make the rotation obvious. */
export function EngineLoader({ size = 64 }: { size?: number }) {
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      {/* Nacelle cowl + inlet lip */}
      <circle cx="50" cy="50" r="48" className="av-body" />
      <circle cx="50" cy="50" r="44.5" className="av-stroke" />
      <circle cx="50" cy="50" r="41" className="av-soft-fill" />
      <g className="av-engine__fan">
        {Array.from({ length: FAN_BLADES }, (_, i) => (
          <path
            key={i}
            d={FAN_BLADE}
            className={i === 0 ? 'av-engine__blade av-engine__blade--lit' : 'av-engine__blade'}
            transform={`rotate(${(360 / FAN_BLADES) * i} 50 50)`}
          />
        ))}
        <circle cx="50" cy="50" r="12" className="av-body" />
        <path d="M50 43.5 A6.5 6.5 0 0 1 56.5 50" className="av-engine__swirl" />
      </g>
    </svg>
  );
}

const HYDSYS_ROUTE = 'M36 64 H112';

/** Reservoir → engine-driven pump → accumulator → selector valve → double-acting actuator,
 *  with the return line back to the reservoir. */
export function HydraulicSystemLoader({ width = 200 }: { width?: number }) {
  return (
    <svg className="av-scene" width={width} height={width / 2} viewBox="0 0 200 100" aria-hidden="true">
      <path d={HYDSYS_ROUTE} className="av-line" />
      <path d="M150 76 V88 H22 V78" className="av-stroke" />
      <path d="M78 64 V40" className="av-line" />
      {[0, 1, 2].map((i) => (
        <circle key={i} r="2.4" className="av-dot" style={{ offsetPath: `path('${HYDSYS_ROUTE}')`, animationDelay: `${-i * 1.6}s` }} />
      ))}
      {/* Reservoir with fluid */}
      <rect x="8" y="46" width="28" height="32" rx="4" className="av-body" />
      <rect x="11" y="60" width="22" height="15" rx="2" className="av-fluid" />
      {/* Pump: rotor turns */}
      <circle cx="54" cy="64" r="10" className="av-body" />
      <g className="av-spin" style={{ transformOrigin: '54px 64px' }}>
        <line x1="54" y1="56" x2="54" y2="72" className="av-stroke" />
        <line x1="46" y1="64" x2="62" y2="64" className="av-stroke" />
      </g>
      {/* Accumulator on the pressure line */}
      <rect x="70" y="14" width="16" height="28" rx="8" className="av-body" />
      <rect x="72" y="28" width="12" height="11" rx="4" className="av-fluid" />
      {/* Selector valve */}
      <rect x="92" y="54" width="18" height="20" rx="2" className="av-body" />
      <path d="M96 60 L106 68 M96 68 L106 60" className="av-stroke" />
      {/* Double-acting actuator: barrel, fluid chamber, piston + rod */}
      <rect x="110" y="52" width="62" height="24" rx="4" className="av-body" />
      <rect x="113" y="55" width="6" height="18" rx="1.5" className="av-fluid av-hyd__chamber" />
      <g className="av-hyd__piston">
        <rect x="119" y="54" width="5" height="20" rx="1.5" className="av-base-fill" />
        <rect x="124" y="62" width="58" height="4" rx="2" className="av-base-fill" />
        <circle cx="184" cy="64" r="4" className="av-body" />
      </g>
    </svg>
  );
}

const ELEC_ROUTE = 'M38 50 H150';

/** Generator → transformer-rectifier unit → contactor → battery on the DC bus. No plug:
 *  aircraft power comes from a generator and a battery. */
export function ElectricalLoader({ width = 200 }: { width?: number }) {
  return (
    <svg className="av-scene" width={width} height={width / 2} viewBox="0 0 200 100" aria-hidden="true">
      {/* Panel: the contactor arm and its stroked parts move over a body, not the grey stage. */}
      <rect x="1" y="6" width="198" height="88" rx="10" className="av-body" />
      <path d={ELEC_ROUTE} className="av-line" />
      {[0, 1, 2].map((i) => (
        <circle key={i} r="2.2" className="av-glow-dot" style={{ offsetPath: `path('${ELEC_ROUTE}')`, animationDelay: `${-i * 2}s` }} />
      ))}
      {/* Generator (AC) */}
      <circle cx="24" cy="50" r="14" className="av-body" />
      <path d="M15 50 C18 43 21 43 24 50 S30 57 33 50" className="av-stroke" />
      {/* Transformer-rectifier unit: AC in, DC out */}
      <rect x="62" y="37" width="26" height="26" rx="3" className="av-body" />
      <path d="M62 63 L88 37" className="av-stroke" />
      <path d="M65 45 C67 42 69 42 70 45 S73 48 75 45" className="av-stroke" />
      <path d="M76 56 H85 M76 59.5 H85" className="av-stroke" />
      {/* Contactor: the arm closes onto the bus, then opens again */}
      <circle cx="104" cy="50" r="2.6" className="av-base-fill" />
      <circle cx="124" cy="50" r="2.6" className="av-base-fill" />
      <line x1="104" y1="50" x2="123" y2="50" className="av-elec__contact" />
      {/* Battery */}
      <rect x="150" y="38" width="38" height="26" rx="3" className="av-body" />
      <rect x="156" y="33" width="7" height="5" rx="1" className="av-base-fill" />
      <rect x="175" y="33" width="7" height="5" rx="1" className="av-base-fill" />
      <path d="M156.5 51 H162.5 M159.5 48 V54 M175.5 51 H181.5" className="av-stroke" />
    </svg>
  );
}

/** Control column → cable over a pulley → elevator: the column moves fore and aft and the
 *  elevator follows, in step. */
export function FlightControlLoader({ width = 200 }: { width?: number }) {
  return (
    <svg className="av-scene" width={width} height={width / 2} viewBox="0 0 200 100" aria-hidden="true">
      {/* Panel: the column and pulley strokes move over a body, not the grey stage (2026-10-08 rule). */}
      <rect x="1" y="6" width="198" height="88" rx="10" className="av-body" />
      {/* Floor + column pivot */}
      <line x1="14" y1="86" x2="50" y2="86" className="av-stroke" />
      <g className="av-fc__column">
        <line x1="30" y1="84" x2="30" y2="36" className="av-stroke" />
        <path d="M20 30 V38 H40 V30" className="av-stroke" />
      </g>
      <circle cx="30" cy="84" r="3.4" className="av-base-fill" />
      {/* Cable run over the pulley to the elevator horn */}
      <path d="M33 72 H75 M89 72 H160 V60" className="av-line" />
      <circle cx="82" cy="72" r="8" className="av-body" />
      <g className="av-fc__pulley">
        <line x1="82" y1="66" x2="82" y2="78" className="av-stroke" />
      </g>
      {/* Horizontal stabiliser + hinged elevator */}
      <path d="M134 56 Q146 49 172 53.5 L172 58.5 Q146 63 134 56 Z" className="av-body" />
      <path d="M172 53.5 L195 56 L172 58.5 Z" className="av-fc__elevator" />
    </svg>
  );
}

// ─── Parts (small cards) ──────────────────────────────────────────────────────────────

const PULLEY_CABLE = 'M46.7 20.3 L77.1 63.5';

/** Two cable pulleys turning, a glow running along the cable between them. */
export function PulleyLoader({ size = 64 }: { size?: number }) {
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <path d="M21.3 39.7 L54.9 80.5" className="av-stroke" />
      <path d={PULLEY_CABLE} className="av-stroke" />
      <circle r="2.4" className="av-glow-dot" style={{ offsetPath: `path('${PULLEY_CABLE}')` }} />
      {([[34, 30, 16], [66, 72, 14]] as const).map(([cx, cy, r]) => (
        <g key={cx}>
          <circle cx={cx} cy={cy} r={r} className="av-body" />
          <circle cx={cx} cy={cy} r={r - 5} className="av-stroke" />
          <g className="av-part__spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
            <line x1={cx} y1={cy - (r - 5)} x2={cx} y2={cy + (r - 5)} className="av-stroke" />
            <line x1={cx - (r - 5)} y1={cy} x2={cx + (r - 5)} y2={cy} className="av-stroke" />
          </g>
          <circle cx={cx} cy={cy} r="3.6" className="av-base-fill" />
        </g>
      ))}
    </svg>
  );
}

/** Double-acting cylinder: a port at each end; the piston strokes out and back as fluid
 *  fills one side and leaves the other. */
export function DoubleActingCylinderLoader({ size = 64 }: { size?: number }) {
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <path d="M22 36 V24 M62 36 V24" className="av-line" />
      <rect x="14" y="36" width="56" height="28" rx="4" className="av-body" />
      <rect x="16" y="39" width="8" height="22" rx="1.5" className="av-fluid av-dac__cap" />
      <rect x="29" y="39" width="39" height="22" rx="1.5" className="av-fluid av-dac__rod" />
      <g className="av-dac__piston">
        <rect x="24" y="38" width="5" height="24" rx="1.5" className="av-base-fill" />
        <rect x="29" y="48" width="50" height="4" rx="2" className="av-base-fill" />
        <circle cx="82" cy="50" r="4" className="av-body" />
      </g>
    </svg>
  );
}

const DIODE_ROUTE = 'M10 50 H90';

/** Transfer diode: current pulses pass one way only, through the diode and on. */
export function TransferDiodeLoader({ size = 64 }: { size?: number }) {
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <path d={DIODE_ROUTE} className="av-line" />
      {[0, 1].map((i) => (
        <circle key={i} r="2.4" className="av-glow-dot" style={{ offsetPath: `path('${DIODE_ROUTE}')`, animationDelay: `${-i * 3}s` }} />
      ))}
      <rect x="32" y="32" width="38" height="36" rx="4" className="av-body" />
      <path d="M41 39 L60 50 L41 61 Z" className="av-base-fill" />
      <line x1="61" y1="38" x2="61" y2="62" className="av-stroke av-diode__bar" />
      <circle cx="10" cy="50" r="4" className="av-body" />
      <circle cx="90" cy="50" r="4" className="av-body" />
    </svg>
  );
}

/** Piston accumulator: gas charge above, hydraulic fluid below; the floating piston rises
 *  and falls as fluid is stored and released. */
export function AccumulatorLoader({ size = 64 }: { size?: number }) {
  const clipId = `av-acc-${useId().replace(/:/g, '')}`;
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <rect x="45" y="6" width="10" height="8" rx="2" className="av-base-fill" />
      <path d="M50 86 V94 H80" className="av-line" />
      <rect x="32" y="14" width="36" height="72" rx="16" className="av-body" />
      <clipPath id={clipId}><rect x="34" y="16" width="32" height="68" rx="14" /></clipPath>
      <g clipPath={`url(#${clipId})`}>
        <g className="av-acc__float">
          <rect x="34" y="62" width="32" height="40" className="av-fluid" />
          <rect x="34" y="57" width="32" height="6" rx="1.5" className="av-base-fill" />
        </g>
      </g>
    </svg>
  );
}

const JACK_THREADS = Array.from({ length: 30 }, (_, i) => -40 + i * 5);

/** Jackscrew: the threaded shaft turns and the nut travels along it. */
export function JackscrewLoader({ size = 64 }: { size?: number }) {
  const clipId = `av-js-${useId().replace(/:/g, '')}`;
  return (
    <svg className="av-scene" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <rect x="40" y="6" width="20" height="6" rx="2" className="av-base-fill" />
      <rect x="45" y="10" width="10" height="74" rx="2" className="av-body" />
      <clipPath id={clipId}><rect x="45" y="10" width="10" height="74" /></clipPath>
      <g clipPath={`url(#${clipId})`}>
        <g className="av-js__threads">
          {JACK_THREADS.map((y) => <line key={y} x1="45" y1={y + 3} x2="55" y2={y} className="av-stroke" />)}
        </g>
      </g>
      <g className="av-js__nut">
        <rect x="32" y="58" width="36" height="14" rx="3" className="av-body av-js__nut-body" />
        <line x1="36" y1="65" x2="64" y2="65" className="av-stroke" />
      </g>
      <rect x="30" y="84" width="40" height="10" rx="3" className="av-body" />
    </svg>
  );
}

/** Renders one scene at a given height (wide scenes scale their width to match). */
export function SceneLoader({ scene, height }: { scene: Scene; height: number }) {
  const loader = renderScene(scene, height);
  // Flag ON: every scene runs at the slower, obviously-loading pace (globals.css `.av-slow`).
  return FF_DASHBOARD_R5 ? <span className="av-slow" style={{ display: 'contents' }}>{loader}</span> : loader;
}

function renderScene(scene: Scene, height: number): ReactNode {
  switch (scene) {
    case 'engine': return <EngineLoader size={height} />;
    case 'hydraulic-system': return <HydraulicSystemLoader width={height * 2} />;
    case 'electrical': return <ElectricalLoader width={height * 2} />;
    case 'flight-controls': return <FlightControlLoader width={height * 2} />;
    case 'pulley': return <PulleyLoader size={height} />;
    case 'double-acting-cylinder': return <DoubleActingCylinderLoader size={height} />;
    case 'transfer-diode': return <TransferDiodeLoader size={height} />;
    case 'accumulator': return <AccumulatorLoader size={height} />;
    case 'jackscrew': return <JackscrewLoader size={height} />;
    case 'hydraulic': return <HydraulicLoader width={height * 2} />;
    case 'fuel': return <FuelLoader width={height * 2} />;
    case 'gears': return <GearLoader width={Math.round(height * 1.2)} />;
    case 'gauge':
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: Math.round(height * 0.12) }}>
          <GaugeLoader size={height} />
          <WaveTrace width={Math.round(height * 0.9)} height={Math.round(height * 0.42)} />
        </span>
      );
    default: return <TurbineLoader size={height} />;
  }
}

function shuffled<T>(list: readonly T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Deals `count` distinct scenes of one kind in random order, after mount (SSR and hydration agree). */
export function useSceneDeck(count: number, kind: SceneKind = 'system'): Scene[] | null {
  const [deck, setDeck] = useState<Scene[] | null>(null);
  useEffect(() => { setDeck(shuffled(scenesFor(kind)).slice(0, count)); }, [count, kind]);
  return deck;
}

/**
 * A page with both main-body and card slots: `systems` for the body, `parts` for the cards.
 * Flag OFF deals both from ONE legacy deck, so no scene repeats across the page (as before).
 */
export function useSceneDecks(systemCount: number, partCount: number): { systems: Scene[]; parts: Scene[] } | null {
  const [decks, setDecks] = useState<{ systems: Scene[]; parts: Scene[] } | null>(null);
  useEffect(() => {
    const systems = scenesFor('system');
    const parts = scenesFor('part');
    if (systems === parts) {
      // One shared deck (flag OFF): deal it once and split it.
      const one = shuffled(systems);
      setDecks({ systems: one.slice(0, systemCount), parts: one.slice(systemCount, systemCount + partCount) });
      return;
    }
    setDecks({ systems: shuffled(systems).slice(0, systemCount), parts: shuffled(parts).slice(0, partCount) });
  }, [systemCount, partCount]);
  return decks;
}

/** A dealt scene, or blank space of the same height until the deck is dealt after mount. */
export function SceneSlot({ scene, height }: { scene: Scene | undefined; height: number }) {
  return scene ? <SceneLoader scene={scene} height={height} /> : <span style={{ display: 'block', height }} aria-hidden="true" />;
}

/**
 * One scene, chosen at random per mount. The scenes themselves are aria-hidden; pass
 * `label` where this loader is the only loading signal to announce one status.
 */
export function AviationScene({ height = 100, label, kind = 'system' }: { height?: number; label?: string; kind?: SceneKind }) {
  const deck = useSceneDeck(1, kind);
  const scene = deck ? <SceneLoader scene={deck[0]} height={height} /> : <span style={{ display: 'block', height }} aria-hidden="true" />;
  // A real box, not display:contents — Safari drops display:contents elements from the
  // accessibility tree, so the status would never be exposed. The label is text content
  // (sr-only) so the live region has something to announce.
  return label ? (
    <span role="status" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {scene}
      <span className="sr-only">{label}</span>
    </span>
  ) : scene;
}

export function FlowLine({ width = '100%', height = 8, delay = 0 }: { width?: number | string; height?: number; delay?: number }) {
  return (
    <span className="av-flow" style={{ width, height, ['--av-flow-delay' as string]: `${delay}s` }} aria-hidden="true">
      <span className="av-flow__dot" />
    </span>
  );
}

/** A soft skeleton bar with a slow light sweep (a blue tint pulse under Reduce Motion). */
export function SkeletonBar({ width = '100%', height = 10, radius = 999, style }: { width?: number | string; height?: number; radius?: number; style?: CSSProperties }) {
  return <span className="av-bar" style={{ width, height, borderRadius: radius, ...style }} aria-hidden="true" />;
}
